import {
  Emphases,
  LabelIds,
  RowColors,
  RowIds,
  RowOffsets,
  SectionIds,
} from "./consts";
import { getVisibleSpecs } from "./isSectionVisible";
import type {
  LabelId,
  LabelRef,
  PriceFacts,
  PriceRow,
  PriceRowSpec,
  PriceSummaryModel,
  PriceValue,
  PriceView,
  Rule,
  RowId,
  SectionId,
} from "./types";

/**
 * Fallback label for rows whose copy is a straight 1:1 with the row.
 * Exhaustive so a new row can't silently render without a label.
 */
const DEFAULT_LABEL_IDS = {
  [RowIds.SpaceRentalReadout]: LabelIds.HourlyPrice,
  [RowIds.DiySpaceRental]: LabelIds.DiySpaceRental,
  [RowIds.DiySpaceRentalRate]: LabelIds.DiySpaceRentalRate,
  [RowIds.FsvSpaceRental]: LabelIds.FsvSpaceRental,
  [RowIds.DayRateDiscountReadout]: LabelIds.HourlyDiscount,
  [RowIds.DayRateDiscount]: LabelIds.DayRateDiscount,
  [RowIds.Entitlement]: LabelIds.Entitlement,
  [RowIds.FoodDrinkMinimum]: LabelIds.FoodDrinkMinimum,
  [RowIds.AddOnsTotal]: LabelIds.AddOnsTotal,
  [RowIds.AdditionalPaymentsTotal]: LabelIds.AdditionalPaymentsTotal,
  [RowIds.CleaningFee]: LabelIds.CleaningFee,
  [RowIds.SplitPaymentFee]: LabelIds.SplitPaymentFee,
  [RowIds.ProcessingFee]: LabelIds.ProcessingFee,
  [RowIds.Taxes]: LabelIds.Taxes,
  [RowIds.Credits]: LabelIds.Credits,
  [RowIds.TerminalBookingOffset]: LabelIds.TerminalBooking,
  [RowIds.BookingTotal]: LabelIds.Total,
  [RowIds.StaffGratuity]: LabelIds.StaffGratuityPercent,
  [RowIds.HostInlineServiceFee]: LabelIds.ServiceFee,
  [RowIds.VatTaxes]: LabelIds.Taxes,
  [RowIds.PaymentAmount]: LabelIds.PaymentAmount,
  [RowIds.DueAtVenueInline]: LabelIds.DueAtVenue,
  [RowIds.EarningsSubtotal]: LabelIds.EarningsSubtotal,
  [RowIds.HostFee]: LabelIds.ServiceFee,
  [RowIds.EstimatedEarnings]: LabelIds.EstimatedEarnings,
  [RowIds.PayoutFromPeerspace]: LabelIds.PayoutFromPeerspace,
  [RowIds.GuestSpendAtVenue]: LabelIds.GuestSpendAtVenue,
  [RowIds.DueToday]: LabelIds.DueToday,
  [RowIds.PaidToDate]: LabelIds.AlreadyPaid,
  [RowIds.DifferenceDueToday]: LabelIds.DifferenceDueToday,
  [RowIds.Refund]: LabelIds.Refund,
  [RowIds.UpcomingCharge]: LabelIds.UpcomingCharge,
  [RowIds.DueAtVenue]: LabelIds.DueAtVenue,
  [RowIds.CollectedFoodDrinkMinimum]: LabelIds.FoodDrinkMinimum,
  [RowIds.CollectedAddOnsTotal]: LabelIds.CollectedAddOnsTotal,
  [RowIds.CollectedGuestSpendAtVenue]: LabelIds.GuestSpendAtVenue,
} as const satisfies Record<RowId, LabelId>;

/*  The resolve helper functions below are used to determine if
 *  a label should be displayed based on the facts and view.
 *  - resolveRule: Determines if a rule should be displayed based on the facts and view.
 *  - resolveValueCaption: Determines if a value caption should be displayed based on the facts and view.
 *  - resolveValue: Determines if a value should be displayed based on the facts and view.
 *  - buildRow: Builds a row based on the facts and view.
 *  - buildPriceSummary: Builds a price summary based on the facts and view.
 */

/*  The resolveRule helper function is used to determine if a rule should be displayed based on the facts and view.
 *  - rule: The rule to evaluate.
 *  - facts: The facts to evaluate the rule against.
 *  - view: The view to evaluate the rule against.
 *  - fallback: The fallback value to return if the rule is undefined.
 *  - return: The value of the rule.
 */

/** Evaluates a spec (PriceRowSpec) some fields are constant, others are dynamic based on the facts and view. */
const resolveRule = <T>(
  rule: Rule<T> | undefined,
  facts: PriceFacts,
  view: PriceView,
  fallback: T,
): T => {
  if (rule === undefined) return fallback;
  return typeof rule === "function"
    ? (rule as (facts: PriceFacts, view: PriceView) => T)(facts, view)
    : rule;
};

/** Caption under the value: LabelId, LabelRef, or `(facts, view) =>` (omit if undefined). */
const resolveValueCaption = ({
  facts,
  rule,
  view,
}: {
  facts: PriceFacts;
  rule: PriceRowSpec["valueCaption"];
  view: PriceView;
}): LabelRef | undefined => {
  if (rule === undefined) return undefined;
  if (typeof rule === "string") return { id: rule };
  if (typeof rule === "function") {
    return rule(facts, view) ?? undefined;
  }
  return rule;
};

/** Right-hand value: custom `spec.value`, else money (or `zeroAs` label at 0). */
const resolveValue = ({
  facts,
  spec,
  view,
}: {
  facts: PriceFacts;
  spec: PriceRowSpec;
  view: PriceView;
}): PriceValue => {
  if (spec.value) {
    return spec.value(facts, view);
  }

  const amount = spec.amount(facts, view) ?? 0;
  if (amount === 0 && spec.zeroAs != null) {
    return { kind: "label", ref: { id: spec.zeroAs } };
  }
  return { kind: "money", amount };
};

const buildRow = ({
  dividerBefore,
  facts,
  spec,
  view,
}: {
  dividerBefore: boolean;
  facts: PriceFacts;
  spec: PriceRowSpec;
  view: PriceView;
}): PriceRow => {
  const amount = spec.amount(facts, view) ?? 0;
  const strikethrough = spec.strikethrough?.(facts) ?? undefined;
  const caption =
    spec.caption === undefined
      ? undefined
      : typeof spec.caption === "function"
        ? (spec.caption(facts, view) ?? undefined)
        : spec.caption;

  return {
    id: spec.id,
    label:
      typeof spec.label === "function"
        ? spec.label(facts, view)
        : (spec.label ?? { id: DEFAULT_LABEL_IDS[spec.id] }),
    value: resolveValue({ facts, spec, view }),
    caption,
    valueCaption: resolveValueCaption({
      facts,
      rule: spec.valueCaption,
      view,
    }),
    emphasis: resolveRule(spec.emphasis, facts, view, Emphases.Default),
    color: resolveRule(spec.color, facts, view, RowColors.Default),
    offset: resolveRule(spec.offset, facts, view, RowOffsets.None),
    dividerBefore,
    group: spec.group,
    negative: spec.negative,
    strikethrough:
      strikethrough != null && strikethrough !== amount
        ? strikethrough
        : undefined,
    tooltipId: resolveRule(spec.tooltipId, facts, view, undefined),
    detail: resolveRule(spec.detail, facts, view, undefined),
  };
};

const SECTION_ORDER = [
  SectionIds.PriceBreakdown,
  SectionIds.HostEarnings,
  SectionIds.PaymentPayout,
  SectionIds.CollectedAtVenue,
] as const satisfies readonly SectionId[];

/**
 * Filters `PRICE_ROWS` by section visibility + amount/`show`, maps to semantic
 * `PriceRow`s, and groups into sections. Sets `dividerBefore` on group change
 * within a section.
 *
 * Amount null/undefined hides the row. Amount 0 hides unless `allowZero` or
 * `zeroAs` is set.
 *
 * When `view.inquiryOutsideBase` is set, returns an empty section list plus
 * `outsideBaseMessage` for bindings / adapter chrome.
 */
const buildPriceSummary = (
  facts: PriceFacts,
  view: PriceView,
): PriceSummaryModel => {
  if (view.inquiryOutsideBase != null) {
    return {
      sections: [],
      outsideBaseMessage: {
        minimumDuration: view.inquiryOutsideBase.minimumDuration,
        perspective: view.perspective,
      },
    };
  }

  const visibleSpecs = getVisibleSpecs({ facts, view });

  const rowsBySection = new Map<SectionId, PriceRow[]>();
  for (const sectionId of SECTION_ORDER) {
    rowsBySection.set(sectionId, []);
  }

  let previousGroup: PriceRow["group"] | null = null;
  let previousSection: SectionId | null = null;

  for (const spec of visibleSpecs) {
    const sectionChanged = previousSection !== spec.section;
    const dividerBefore =
      !sectionChanged && previousGroup != null && previousGroup !== spec.group;

    const row = buildRow({ dividerBefore, facts, spec, view });
    rowsBySection.get(spec.section)?.push(row);
    previousGroup = spec.group;
    previousSection = spec.section;
  }

  const sections = SECTION_ORDER.map((id) => ({
    id,
    rows: rowsBySection.get(id) ?? [],
  })).filter((section) => section.rows.length > 0);

  return { sections };
};

export { buildPriceSummary };
